import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Booking } from '../../api/model/booking';
import { BookingService } from '../../api/resources/booking/booking.service';
import { BookingCalculation } from '../../api/model/booking-calculation';
import { AgentService } from '../../api/resources/agent/agent.service';
import { TransportIteraction } from '../../api/model/transport-iteraction';
import * as moment from 'moment';
import { ToasterService } from '../../global/toaster.service';
import { UserService } from '../../api/resources/user/user.service';
import { AgentReservationService } from '../../api/resources/agent-reservation/agent-reservation.service';
import { StaticUtils } from '../../utils/static-utils';
import { GenderPrice } from '../../api/model/gender-price';

@Component({
  selector: 'app-booking-details',
  templateUrl: './booking-details.component.html',
  styleUrls: ['./booking-details.component.scss']
})

export class BookingDetailsComponent implements OnInit {
  @Input('supplierId') supplierId: number;
  @Input('bookingId') bookingId: number;
  
  booking: Booking;
  utils = StaticUtils;
  calculatedPrice: BookingCalculation;
  downloadingTicket = false;
  sendingTicket = false;
  pickupTransfer: TransportIteraction;
  dropoffTransfer: TransportIteraction;
  transport: object = {};
  hasTransport: boolean = false;
  hasExtras: boolean = false;
  moment = moment;
  loadingBooking = true;
  refundCode: string;

  constructor(
    public activeModal: NgbActiveModal,
    private _bookingService: BookingService,
    private _agentService: AgentService,
    private _agentReservationService: AgentReservationService,
    private _toasterService: ToasterService,
    private _userService: UserService) {}

  ngOnInit() {
    this.init();
  }

  init() {
    this.loadingBooking = true;
    this._agentReservationService.findReservationBySupplier(this.supplierId, this.bookingId).subscribe(
      (booking) => {
        this.booking = booking;
        this.addNotesToTransport();

        this._bookingService.getCalculatedPrice(this.booking, this.booking.creatorSupplier).subscribe(
          (calculatedPrice) => this.calculatedPrice = calculatedPrice,
          undefined,
          () => this.loadingBooking = false
        );

        if (this.booking.transport && this.booking.transport.transfers) {
          for (const transfer of this.booking.transport.transfers) {
            if (transfer.transportDirection === 1) {
              this.pickupTransfer = transfer;
            } else if (transfer.transportDirection === 2) {
              this.dropoffTransfer = transfer;
            }
          }
        }

        if (this.booking.bookingComponents) {
          for (const transport of this.booking.bookingComponents) {
            if (transport.component.componentType === 1) {
              this.transport = transport;
              this.hasTransport = true;
            } else if (transport.component.componentType === 0) {
              this.hasExtras = true;
            }
          }
        }
      },
      undefined,
      () => this.loadingBooking = !!this.booking
    );
  }

  addNotesToTransport() {
    if (this.booking.transport.transfers && this.booking.transport.transfers.length > 0) {
        this.booking.transport.transfers.forEach(transfer => {
            if (transfer && transfer.route && transfer.stop) {
                const routeSelected = this.booking.event.eventRoutes.find(route => route.id === transfer.route.id);
               
                if (routeSelected && routeSelected.iteneraries) {
                    const itenerary = routeSelected.iteneraries.find(ite => ite.stop.id === transfer.stop.id);
                    
                    if (itenerary) {
                        transfer.stop.note = itenerary.note;
                    }
                }
            }
        });
    }
  }

  downloadTicket() {
    this.downloadingTicket = true;
    this._agentService.downloadTicket(this.booking.id, this.booking.event.supplierId).subscribe(
      (data) => {
        function base64ToArrayBuffer(base64) {
          const binaryString = window.atob(base64);
          const binaryLen = binaryString.length;
          const bytes = new Uint8Array(binaryLen);
          for (let i = 0; i < binaryLen; i++) {
            const ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
          }
          return bytes;
        }
        function saveByteArray(reportName, byte) {
          const blob = new Blob([byte]);
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          const fileName = reportName + '.pdf';
          link.download = fileName;
          link.click();
        }

        const dataArr = base64ToArrayBuffer(data);
        saveByteArray('ticket_' + this.ticketNumber, dataArr);
        this.downloadingTicket = false;
      },
      undefined,
      () => this.downloadingTicket = false
    );
  }

  sendTicket() {
    this.sendingTicket = true;
    this._agentService.sendTicket(this.booking.id, this.booking.event.supplierId).subscribe({
      next: () => {
        this._toasterService.showInfo('Ticket sent successfully.');
      },
      error: () => {
        this.sendingTicket = false;
      },
      complete: () => {
        this.sendingTicket = false;
      }
    });
  }

  goToBooking(id: number) {
    this.loadingBooking = true;
    this._agentReservationService.findBooking(id).subscribe(
      (bookings: Booking | Booking[]) => {
        let booking: Booking | undefined;

        if (bookings instanceof Array) {
          if (bookings.length > 0) {
            booking = bookings[0];
          }
        } else {
          booking = bookings;
        }

        if (booking) {
          this.supplierId = booking.event.supplierId;
          this.bookingId = booking.id;
        }
        this.init();
      },
      undefined,
      () => this.loadingBooking = !!this.booking
    );
  }

  getGenderPriceLabel(genderPrice: GenderPrice): string {
    return genderPrice.audienceDescription || genderPrice.audienceTypeDescription || 'Tickets';
  }

  formatDepositDescription(paymentDepositType: number, depositType: number): string {
    let result = '';

    if (paymentDepositType === 2 && depositType === 2) {
      result = ' (Same as commission)';
    } else if (paymentDepositType === 1) {
      result = ' (Full payment)';
    }

    return result;
  }

  getRefundCancelationReasonLabel(refundCancelationReasonType: number, refundCancelationReasonDescription: string) : string {    
    const reasonLabels: { [key: number]: string } = {
      0: 'Other',
      1: 'Supplier mistake',
      2: 'Customer changed his mind',
    };

    const label = reasonLabels[refundCancelationReasonType];
  
    if (label === undefined) {
      return 'Unknown reason';
    }
  
    return label;
  }

  getRefundReasonLabel(refundReasonTypeId: number): string {
    const reasonLabels: { [key: number]: string } = {
      0: 'Other',
      1: 'Climate conditions',
      2: 'Equipment failure',
      3: 'Minimum number not met',
    };

    const label = reasonLabels[refundReasonTypeId];
  
    if (label === undefined) {
      return 'Unknown reason';
    }

    return label;
  }

  getRefundItemsNames(items: any[]): string {
    const itemsLabels = items
      .filter((item) => item.bookingGenderPrice.quantityToBook > 0)
      .map((item) => item.bookingGenderPrice.quantityToBook + ' x ' + item.bookingGenderPrice.audienceTypeDescription);
    return itemsLabels.join(', ');
  }

  applyRefundCode(): void {
    this._agentReservationService.confirmBookingRefundCode(this.supplierId, this.bookingId, this.refundCode)
      .subscribe(() => {
        this.refundCode = '';
        this.init();
      });
  }

  get ticketNumber(): string {
    let result = '#';
    // if (this.booking !== undefined && !this.booking.event.product.hasManualConfirmation) {
    //   result = '#' + '0'.repeat(5 - this.booking.id.toString().length) + this.booking.id;
    // }
    if (this.booking !== undefined) {
      result = '#' + this.booking.id;
    }
    return result;
  }

  get showComissions(): boolean {
    return this._userService.userHasAccess('booking-comissions');
  }

  get creatorAgentBrandName(): string {
    let result = 'Unknown';
    if (this.booking && this.booking.creatorAgent && this.booking.creatorAgent.brandName) {
      result = this.booking.creatorAgent.brandName;
    }
    return result;
  }

  get reservationDate(): string {
    let result = 'an unknown date';
    if (this.booking && this.booking.reservationDate) {
      result = this.utils.formatDateTime(moment(this.booking.reservationDate));
    }
    return result;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/atoms/booking-details/booking-details.component.ts